<template>
    <div class="work-order-form">
        <!-- Main Form Tabs -->
        <div class="d-flex align-items-center">
            <ul class="nav nav-tabs flex-grow-1" role="tablist">
                <li class="nav-item" role="presentation">
                    <button 
                        class="nav-link" 
                        :class="{ active: activeFormTab === 'header' }"
                        @click="activeFormTab = 'header'"
                        type="button"
                    >
                        Invoice Details
                    </button>
                </li>
                <li class="nav-item" role="presentation">
                    <button 
                        class="nav-link" 
                        :class="{ active: activeFormTab === 'billing' }"
                        @click="activeFormTab = 'billing'"
                        type="button"
                    >
                        Billing & Payment
                    </button>
                </li>
                <li class="nav-item" role="presentation">
                    <button 
                        class="nav-link" 
                        :class="{ active: activeFormTab === 'notes' }"
                        @click="activeFormTab = 'notes'"
                        type="button"
                    >
                        Notes & Terms
                    </button>
                </li>
            </ul>
            <button 
                @click="printInvoice()" 
                class="btn btn-sm btn-outline-info ms-2 mb-1"
                title="Print Invoice"
                type="button"
            >
                <i class="fa fa-print"></i> Print 
            </button>
        </div>

        <div class="tab-content border border-top-0 p-3 bg-light">
            <!-- Invoice Details Tab -->
            <div v-show="activeFormTab === 'header'">
                <div class="row">
                    <div class="col-md-4">
                        <kt-select
                            label="Type"
                            v-model="model.Title"
                            :options="titleOptions"
                        ></kt-select>
                    </div>
                    <div class="col-md-4">
                        <label class="d-block">
                            <strong>Client</strong>
                        </label>
                        <client-ac 
                            v-model="model.ContactId" 
                            input-class="form-control form-control-sm" 
                        ></client-ac>
                    </div>
                    <div class="col-md-4">
                        <label class="d-block">
                            <strong>Related Request/Job</strong>
                        </label>
                        <div v-if="showJobField || model.RequestId">
                            <job-ac 
                                v-model="model.RequestId" 
                                :client="model.ContactId"
                                input-class="form-control form-control-sm" 
                            ></job-ac>
                        </div>
                        <div v-else>
                            <button 
                                type="button"
                                class="btn btn-sm btn-outline-secondary"
                                @click="showJobField = true"
                            >
                                <i class="fas fa-link"></i> Link related job
                            </button>
                        </div>
                    </div>
                </div>

                <div class="row mt-3">
                    <div class="col-md-3">
                        <kt-input
                            type="text"
                            label="Invoice Number" 
                            input-class="form-control form-control-sm"
                            v-model="model.InvoiceNumber"
                            placeholder="Auto-generated if blank"
                        ></kt-input>
                    </div>
                    <div class="col-md-3">
                        <kt-input
                            type="text"
                            label="PO Number" 
                            input-class="form-control form-control-sm"
                            v-model="model.PONumber"
                            placeholder="Purchase Order #"
                        ></kt-input>
                    </div>
                    <div class="col-md-3">
                        <kt-select
                            label="Status"
                            v-model="model.Status"
                            :options="statusOptions"
                        ></kt-select>
                    </div>
                    <div class="col-md-3">
                        <kt-select
                            label="Payment Status"
                            v-model="model.PaymentStatus"
                            :options="paymentStatusOptions"
                        ></kt-select>
                    </div>
                </div>

                <div class="row mt-3">
                    <div class="col-md-4">
                        <kt-input
                            type="date"
                            label="Date Created" 
                            input-class="form-control form-control-sm"
                            v-model="dateCreated"
                        ></kt-input>
                    </div>
                    <div class="col-md-4">
                        <kt-input
                            type="date"
                            label="Due Date" 
                            input-class="form-control form-control-sm"
                            v-model="dateDue"
                        ></kt-input>
                    </div>
                    <div class="col-md-4">
                        <kt-input
                            type="date"
                            label="Date Completed" 
                            input-class="form-control form-control-sm"
                            v-model="dateCompleted"
                        ></kt-input>
                    </div>
                </div>
            </div>

            <!-- Billing & Payment Tab -->
            <div v-show="activeFormTab === 'billing'">
                <div class="row">
                    <div class="col-md-12">
                        <h5 class="mb-3"><strong>Invoice Totals</strong></h5>
                        <div class="row">
                                    <div class="col-md-4">
                                        <kt-input
                                            type="number"
                                            label="Subtotal" 
                                            input-class="form-control form-control-sm"
                                            placeholder="0.00"
                                            v-model="model.Subtotal"
                                            :step="0.01"
                                            readonly
                                        ></kt-input>
                                        <small class="text-muted">Calculated from line items</small>
                                    </div>
                                    <div class="col-md-4">
                                        <kt-input
                                            type="number"
                                            label="Tax Amount" 
                                            input-class="form-control form-control-sm"
                                            placeholder="0.00"
                                            v-model="model.Tax"
                                            :step="0.01"
                                            readonly
                                        ></kt-input>
                                        <small class="text-muted">Calculated from line items</small>
                                    </div>
                                    <div class="col-md-4">
                                        <kt-input
                                            type="number"
                                            label="Total" 
                                            input-class="form-control form-control-sm"
                                            placeholder="0.00"
                                            v-model="model.Total"
                                            :step="0.01"
                                            readonly
                                        ></kt-input>
                                        <small class="text-muted">Subtotal + Tax</small>
                                    </div>
                                </div>

                                <div class="row mt-3">
                                    <div class="col-md-4">
                                        <kt-input
                                            type="number"
                                            label="Amount Paid" 
                                            input-class="form-control form-control-sm"
                                            placeholder="0.00"
                                            v-model="model.AmountPaid"
                                            :step="0.01"
                                        ></kt-input>
                                    </div>
                                    <div class="col-md-4">
                                        <kt-input
                                            type="date"
                                            label="Date Paid" 
                                            input-class="form-control form-control-sm"
                                            v-model="datePaid"
                                        ></kt-input>
                                    </div>
                                    <div class="col-md-4">
                                        <kt-input
                                            type="number"
                                            label="Balance Due" 
                                            input-class="form-control form-control-sm"
                                            placeholder="0.00"
                                            :model-value="balanceDue"
                                            :step="0.01"
                                            readonly
                                        ></kt-input>
                                        <small class="text-muted">Total - Amount Paid</small>
                                    </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Notes & Terms Tab -->
            <div v-show="activeFormTab === 'notes'">
                <div class="row">
                    <div class="col-md-6">
                        <label><strong>Internal Notes</strong></label>
                        <textarea 
                            class="form-control" 
                            v-model="model.InternalNotes"
                            rows="8"
                            placeholder="Internal notes (not visible to client)"
                        ></textarea>
                    </div>
                    <div class="col-md-6">
                        <label><strong>Terms & Conditions</strong></label>
                        <textarea 
                            class="form-control" 
                            v-model="model.Terms"
                            rows="8"
                            placeholder="Payment terms, warranty info, etc."
                        ></textarea>
                    </div>
                </div>
            </div>
        </div>

        <!-- Line Items Section (if work order exists) -->
        <div class="row mt-4" v-if="model.Id">
            <div class="col">
                <ul class="nav nav-tabs" role="tablist">
                    <li class="nav-item" role="presentation">
                        <button 
                            class="nav-link active" 
                            type="button"
                        >
                            Line Items
                        </button>
                    </li>
                </ul>

                <div class="tab-content border border-top-0 p-3 bg-light">
                    <work-order-items-datatable
                        :work-order-id="model.Id"
                        :request-id="model.RequestId"
                        :client-id="model.ContactId"
                        :embedded="true"
                        @items-updated="handleItemsUpdated"
                    ></work-order-items-datatable>
                </div>
            </div>
        </div>

        <!-- Save prompt if no ID -->
        <div v-else class="alert alert-info mt-3">
            <i class="fas fa-info-circle"></i> &nbsp;
            <strong>Save the work order first</strong> to add line items.
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { WorkOrderModel } from '@/models/WorkOrderModel';
import ClientAc from '@/components/Clients/ClientAc.vue';
import JobAc from '@/components/Jobs/JobAc.vue';
import KtInput from '@/components/UI/KtInput.vue';
import KtSelect from '@/components/UI/KtSelect.vue';
import WorkOrderItemsDatatable from '@/components/WorkOrders/WorkOrderItemsDatatable.vue';
import Helper from '@/services/Helper';
import InvoicePdfService from '@/services/InvoicePdfService';
import Alert from '@/services/Alert';

const route = useRoute();
const activeFormTab = ref('header');
const showJobField = ref(false);

const model = defineModel<WorkOrderModel>({
    default: () => new WorkOrderModel()
});

// Title options
const titleOptions = [
    { label: 'Invoice', value: 'Invoice' },
    { label: 'Partial', value: 'Partial' },
    { label: 'Estimate', value: 'Estimate' }
];

// Status options
const statusOptions = [
    { label: 'Draft', value: 0 },
    { label: 'Pending', value: 1 },
    { label: 'Approved', value: 2 },
    { label: 'Completed', value: 3 },
    { label: 'Cancelled', value: 4 }
];

const paymentStatusOptions = [
    { label: 'Unpaid', value: 'Unpaid' },
    { label: 'Partial', value: 'Partial' },
    { label: 'Paid', value: 'Paid' }
];

// Date helpers (convert between Date objects and string format)
const dateCreated = computed({
    get: () => model.value.DateCreated || '',
    set: (val) => { model.value.DateCreated = val || null; }
});

const dateDue = computed({
    get: () => model.value.DateDue || '',
    set: (val) => { model.value.DateDue = val || null; }
});

const dateCompleted = computed({
    get: () => model.value.DateCompleted || '',
    set: (val) => { model.value.DateCompleted = val || null; }
});

const datePaid = computed({
    get: () => model.value.DatePaid || '',
    set: (val) => { model.value.DatePaid = val || null; }
});

// Computed balance due
const balanceDue = computed(() => {
    const total = model.value.Total || 0;
    const paid = model.value.AmountPaid || 0;
    return (total - paid).toFixed(2);
});

// Handle line items update (recalculate totals)
const handleItemsUpdated = (items: any[]) => {
    let subtotal = 0;
    let taxTotal = 0;

    items.forEach(item => {
        const lineTotal = (item.Quantity || 0) * (item.UnitCost || 0);
        const discount = item.DiscountAmount || 0;
        const taxableAmount = lineTotal - discount;
        const tax = item.TaxAmount || 0;

        subtotal += lineTotal - discount;
        taxTotal += tax;
    });

    // Update model with items array (needed for Print Invoice button visibility)
    model.value.Items = items;
    
    model.value.Subtotal = subtotal;
    model.value.Tax = taxTotal;
    model.value.Total = subtotal + taxTotal;
};

// Watch for client changes to clear the request selection
watch(() => model.value.ContactId, (newClient, oldClient) => {
    // If client changed and is different, clear the request
    if (oldClient !== undefined && newClient !== oldClient) {
        model.value.RequestId = null;
    }
});

// Print invoice function
const printInvoice = () => {
    try {
        InvoicePdfService.generateInvoicePDF(model.value);
        Alert.notify({ text: "Invoice generated successfully", type: "success" });
    } catch (error) {
        console.error("Error generating invoice:", error);
        Alert.notify({ text: "Failed to generate invoice", type: "error" });
    }
};

// Initialize from query params if creating new
onMounted(() => {
    if (route.query.requestId) {
        model.value.RequestId = Number(route.query.requestId);
    }
    if (route.query.clientId) {
        model.value.ContactId = Number(route.query.clientId);
    }
    
    // Set default dates for new work orders
    if (!model.value.Id) {
        model.value.DateCreated = Helper.dateToInput(new Date());
        model.value.Status = 0; // Draft
        model.value.PaymentStatus = 'Unpaid'; // Unpaid
    }
});
</script>

<style scoped>
.work-order-form {
    min-height: 400px;
}
</style>
